/* eslint-disable */
/**
 * FearGreedChart — Market Overview Dashboard
 *
 * Contains:
 *   - Crypto Fear & Greed (alternative.me — free, no key)
 *   - Stock Fear & Greed  (CNN internal API — free, no key)
 *   - Total Crypto Market Cap (CoinGecko — free, no key)
 *   - S&P 500, Nasdaq, DJIA (Yahoo Finance via existing /api/mstr-price)
 */
import { useState, useEffect, useRef } from "react";
import { C } from "../shared/globals.js";

const FM = "Montserrat,sans-serif";

const fmtPct = v => (v >= 0 ? "+" : "") + v.toFixed(2) + "%";

const fmtLarge = v => {
  if (v >= 1e12) return "$" + (v / 1e12).toFixed(2) + "T";
  if (v >= 1e9)  return "$" + (v / 1e9).toFixed(2)  + "B";
  if (v >= 1e6)  return "$" + (v / 1e6).toFixed(2)  + "M";
  return "$" + v.toFixed(0);
};

const fmtIndex = v =>
  v >= 1000
    ? v.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })
    : v.toFixed(2);

function fgColor(v) {
  if (v <= 25) return "#ef4444";
  if (v <= 45) return "#f97316";
  if (v <= 55) return "#eab308";
  if (v <= 75) return "#84cc16";
  return "#22c55e";
}
function fgLabel(v) {
  if (v <= 25) return "Extreme Fear";
  if (v <= 45) return "Fear";
  if (v <= 55) return "Neutral";
  if (v <= 75) return "Greed";
  return "Extreme Greed";
}

// ── Mini bar sparkline ────────────────────────────────────────────────────────
function Sparkline({ data }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(260);
  useEffect(() => {
    const obs = new ResizeObserver(es => setW(es[0].contentRect.width || 260));
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);
  if (!data || data.length < 2) return null;
  const H = 44, barW = Math.max(2, (w / data.length) - 1);
  return (
    <div ref={wrapRef} style={{ width:"100%" }}>
      <svg width="100%" height={H} style={{ display:"block" }}>
        {data.map((v, i) => {
          const barH = Math.max(2, (v / 100) * H);
          return <rect key={i} x={(i / data.length) * w} y={H - barH}
            width={barW} height={barH} fill={fgColor(v)} opacity={0.65} rx={1}/>;
        })}
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between",
        fontFamily:FM, fontSize:7, color:"var(--text5)", marginTop:2 }}>
        <span>30d ago</span><span>Today</span>
      </div>
    </div>
  );
}

// ── Gauge ─────────────────────────────────────────────────────────────────────
function Gauge({ value }) {
  const S = 130, cx = S/2, cy = S/2 + 8, r = 48, sw = 9;
  const toRad = d => d * Math.PI / 180;
  const arc = (from, to) => {
    const s = { x: cx + r*Math.cos(toRad(from)), y: cy + r*Math.sin(toRad(from)) };
    const e = { x: cx + r*Math.cos(toRad(to)),   y: cy + r*Math.sin(toRad(to))   };
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${e.x} ${e.y}`;
  };
  const na  = -180 + (value / 100) * 180;
  const nx  = cx + (r - 6) * Math.cos(toRad(na));
  const ny  = cy + (r - 6) * Math.sin(toRad(na));
  const col = fgColor(value);
  const segs = [
    { f:-180, t:-144, c:"#ef4444" }, { f:-144, t:-108, c:"#f97316" },
    { f:-108, t: -90, c:"#eab308" }, { f: -90, t: -54, c:"#84cc16" },
    { f: -54, t:   0, c:"#22c55e" },
  ];
  return (
    <svg width={S} height={S * 0.62} viewBox={`0 0 ${S} ${S * 0.62}`}
      style={{ display:"block", margin:"0 auto" }}>
      <path d={arc(-180, 0)} fill="none" stroke="var(--bg3)" strokeWidth={sw} strokeLinecap="butt"/>
      {segs.map((s, i) => (
        <path key={i} d={arc(s.f, s.t)} fill="none" stroke={s.c}
          strokeWidth={sw} strokeLinecap="butt" opacity={0.25}/>
      ))}
      <path d={arc(-180, na)} fill="none" stroke={col} strokeWidth={sw} strokeLinecap="butt" opacity={0.9}/>
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={col} strokeWidth={2} strokeLinecap="round"/>
      <circle cx={cx} cy={cy} r={3.5} fill={col}/>
      <text x={cx} y={cy - 10} textAnchor="middle" fontFamily={FM}
        fontSize="20" fontWeight="900" fill={col}>{value}</text>
    </svg>
  );
}

// ── Shared card shell ─────────────────────────────────────────────────────────
function Card({ icon, title, loading, error, children }) {
  return (
    <div style={{
      flex:"1 1 210px", minWidth:200, maxWidth:310,
      background:"var(--bg2)", border:"1px solid var(--border)",
      borderRadius:12, padding:"18px 18px 14px",
      display:"flex", flexDirection:"column",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:14 }}>
        <i className={`ph ${icon}`} style={{ fontSize:14, color:"var(--text4)" }}/>
        <span style={{ fontFamily:FM, fontSize:8, fontWeight:700,
          letterSpacing:"0.2em", color:"var(--text4)" }}>{title}</span>
      </div>
      {loading && (
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", minHeight:60 }}>
          <div style={{ width:14, height:14, border:"2px solid var(--border)",
            borderTopColor:C.orange, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
        </div>
      )}
      {error && !loading && (
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:FM, fontSize:10, color:"var(--text5)" }}>
          Unable to load
        </div>
      )}
      {!loading && !error && children}
    </div>
  );
}

// ── Fear & Greed card ─────────────────────────────────────────────────────────
function FearGreedCard({ icon, title, value, history, loading, error }) {
  const col = value !== null ? fgColor(value) : "var(--text4)";
  return (
    <Card icon={icon} title={title} loading={loading} error={error}>
      {value !== null && (
        <>
          <Gauge value={value} />
          <div style={{ textAlign:"center", fontFamily:FM, fontSize:12, fontWeight:800,
            color:col, marginBottom:10 }}>
            {fgLabel(value)}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
            {["Ext. Fear","Fear","Neutral","Greed","Ext. Greed"].map((l, i) => (
              <div key={i} style={{ fontFamily:FM, fontSize:6.5, color:"var(--text5)",
                textAlign:"center", flex:1 }}>{l}</div>
            ))}
          </div>
          {history.length > 0 && (
            <>
              <div style={{ fontFamily:FM, fontSize:7, fontWeight:700,
                letterSpacing:"0.15em", color:"var(--text5)", marginBottom:5 }}>
                30-DAY HISTORY
              </div>
              <Sparkline data={history} />
            </>
          )}
        </>
      )}
    </Card>
  );
}

// ── Total Crypto Market Cap card ──────────────────────────────────────────────
function MarketCapCard({ data, loading, error }) {
  return (
    <Card icon="ph-globe" title="TOTAL CRYPTO MARKET CAP" loading={loading} error={error}>
      {data && (
        <>
          <div style={{ fontFamily:FM, fontSize:26, fontWeight:900,
            color:"var(--text)", letterSpacing:"-0.02em", lineHeight:1, marginBottom:6 }}>
            {fmtLarge(data.totalMarketCap)}
          </div>
          <div style={{ fontFamily:FM, fontSize:12, fontWeight:700,
            color: data.change24h >= 0 ? C.green : C.red, marginBottom:16 }}>
            {fmtPct(data.change24h)}
            <span style={{ fontSize:9, color:"var(--text5)", fontWeight:400, marginLeft:4 }}>24h</span>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[
              { label:"BTC Dominance", val:data.btcDominance, color:C.orange },
              { label:"ETH Dominance", val:data.ethDominance, color:"#60a5fa" },
            ].map(({ label, val, color }) => (
              <div key={label}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ fontFamily:FM, fontSize:8, color:"var(--text4)", fontWeight:700 }}>{label}</span>
                  <span style={{ fontFamily:FM, fontSize:9, fontWeight:700, color }}>{val.toFixed(1)}%</span>
                </div>
                <div style={{ height:3, background:"var(--bg3)", borderRadius:2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${val}%`, background:color, borderRadius:2, opacity:0.8 }}/>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:10, fontFamily:FM, fontSize:7, color:"var(--text5)", letterSpacing:"0.1em" }}>
            24H VOL: {fmtLarge(data.volume24h)}
          </div>
        </>
      )}
    </Card>
  );
}

// ── Stock index card ──────────────────────────────────────────────────────────
function IndexCard({ icon, title, data, loading, error }) {
  return (
    <Card icon={icon} title={title} loading={loading} error={error}>
      {data && (
        <>
          <div style={{ fontFamily:FM, fontSize:22, fontWeight:900,
            color:"var(--text)", letterSpacing:"-0.02em", lineHeight:1, marginBottom:6 }}>
            {fmtIndex(data.price)}
          </div>
          <div style={{ display:"flex", gap:16, marginBottom:16 }}>
            <div>
              <div style={{ fontFamily:FM, fontSize:7, color:"var(--text5)", marginBottom:2, letterSpacing:"0.1em" }}>24H %</div>
              <div style={{ fontFamily:FM, fontSize:12, fontWeight:700,
                color: data.change24h >= 0 ? C.green : C.red }}>
                {fmtPct(data.change24h)}
              </div>
            </div>
            <div>
              <div style={{ fontFamily:FM, fontSize:7, color:"var(--text5)", marginBottom:2, letterSpacing:"0.1em" }}>CHANGE</div>
              <div style={{ fontFamily:FM, fontSize:12, fontWeight:700,
                color: data.change24h >= 0 ? C.green : C.red }}>
                {data.change24h >= 0 ? "+" : ""}{(data.price - data.prev).toFixed(2)}
              </div>
            </div>
          </div>
          <div style={{ marginTop:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between",
              fontFamily:FM, fontSize:7, color:"var(--text5)", marginBottom:3 }}>
              <span>Prev Close</span><span>Now</span>
            </div>
            <div style={{ height:3, background:"var(--bg3)", borderRadius:2, overflow:"hidden" }}>
              <div style={{
                height:"100%",
                width: data.prev > 0
                  ? `${Math.min(100, Math.max(5, Math.abs((data.price / data.prev - 1) * 100) * 10 + 50))}%`
                  : "50%",
                background: data.change24h >= 0 ? C.green : "#ef4444",
                borderRadius:2, opacity:0.8,
              }}/>
            </div>
            <div style={{ fontFamily:FM, fontSize:7, color:"var(--text5)", marginTop:3 }}>
              Prev: {fmtIndex(data.prev)}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FearGreedChart() {
  const [cryptoFG,  setCryptoFG]  = useState({ value:null, history:[], loading:true, error:false });
  const [stockFG,   setStockFG]   = useState({ value:null, history:[], loading:true, error:false });
  const [marketCap, setMarketCap] = useState({ data:null, loading:true, error:false });
  const [spx,    setSpx]    = useState({ data:null, loading:true, error:false });
  const [nasdaq, setNasdaq] = useState({ data:null, loading:true, error:false });
  const [djia,   setDjia]   = useState({ data:null, loading:true, error:false });

  // Crypto F&G
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://api.alternative.me/fng/?limit=30");
        if (!res.ok) throw new Error();
        const json = await res.json();
        const data = json.data || [];
        setCryptoFG({
          value: parseInt(data[0]?.value || 0),
          history: [...data].reverse().map(d => parseInt(d.value)),
          loading:false, error:false,
        });
      } catch { setCryptoFG(p => ({ ...p, loading:false, error:true })); }
    })();
  }, []);

  // Stock F&G (CNN)
  useEffect(() => {
    (async () => {
      try {
        const from = new Date();
        from.setDate(from.getDate() - 30);
        const res = await fetch(
          `https://production.dataviz.cnn.io/index/fearandgreed/graphdata/${from.toISOString().split("T")[0]}`,
          { headers:{ "Accept":"application/json" } }
        );
        if (!res.ok) throw new Error();
        const json = await res.json();
        setStockFG({
          value: Math.round(json.fear_and_greed?.score ?? 0),
          history: (json.fear_and_greed_historical?.data || []).map(d => Math.round(d.y)),
          loading:false, error:false,
        });
      } catch { setStockFG(p => ({ ...p, loading:false, error:true })); }
    })();
  }, []);

  // Total Crypto Market Cap
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://api.coingecko.com/api/v3/global");
        if (!res.ok) throw new Error();
        const json = await res.json();
        const d = json.data;
        setMarketCap({
          data: {
            totalMarketCap: d.total_market_cap.usd,
            change24h: d.market_cap_change_percentage_24h_usd,
            btcDominance: d.market_cap_percentage.btc,
            ethDominance: d.market_cap_percentage.eth,
            volume24h: d.total_volume.usd,
          },
          loading:false, error:false,
        });
      } catch { setMarketCap(p => ({ ...p, loading:false, error:true })); }
    })();
  }, []);

  // Stock indices
  useEffect(() => {
    const fetchIdx = async (ticker, setter) => {
      try {
        const res = await fetch(`/api/mstr-price?ticker=${encodeURIComponent(ticker)}&t=${Date.now()}`);
        if (!res.ok) throw new Error();
        const d = await res.json();
        const price = d.price || 0, prev = d.prev || 0;
        setter({ data:{ price, prev, change24h: prev > 0 ? ((price-prev)/prev)*100 : 0 }, loading:false, error:false });
      } catch { setter({ data:null, loading:false, error:true }); }
    };
    fetchIdx("^GSPC", setSpx);
    fetchIdx("^IXIC", setNasdaq);
    fetchIdx("^DJI",  setDjia);
  }, []);

  return (
    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
      <FearGreedCard icon="ph-currency-btc" title="CRYPTO FEAR & GREED"
        value={cryptoFG.value} history={cryptoFG.history}
        loading={cryptoFG.loading} error={cryptoFG.error} />
      <FearGreedCard icon="ph-chart-line-up" title="STOCK FEAR & GREED"
        value={stockFG.value} history={stockFG.history}
        loading={stockFG.loading} error={stockFG.error} />
      <MarketCapCard data={marketCap.data} loading={marketCap.loading} error={marketCap.error} />
      <IndexCard icon="ph-trend-up"      title="S&P 500"          data={spx.data}    loading={spx.loading}    error={spx.error} />
      <IndexCard icon="ph-desktop-tower" title="NASDAQ COMPOSITE"  data={nasdaq.data} loading={nasdaq.loading} error={nasdaq.error} />
      <IndexCard icon="ph-buildings"     title="DOW JONES (DJIA)"  data={djia.data}   loading={djia.loading}   error={djia.error} />
    </div>
  );
}
